<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getTransactionStatisticsAction } from '@/api/functions/transactions/get-transaction-statistics.action'
import { getTransactionsAction } from '@/api/functions/transactions/get-transactions.action'
import type { ApiPaginationResponse, ApiResponse } from '@/api/typings/response.typing'
import type { Transaction } from '@/api/typings/transaction.typing.ts'
import { useQueryApi } from '@/composables/useApi'
import { useGeneralSettings } from '@/composables/useGeneralSettings'
import TransactionCard from './partials/TransactionCard.vue'

const router = useRouter()
const route = useRoute()

const currentPage = ref(1)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const activeFilters = ref<Record<string, any>>({})

const { transactionsStatusOptions, discountOptions, currencies, countries } = useGeneralSettings()

const {
  data: transactionsData,
  isLoading: isTransactionsLoading,
  refetch: refetchTransactions,
} = useQueryApi<ApiPaginationResponse<Transaction[]>>({
  queryKey: computed(() => ['transactions', currentPage.value, activeFilters.value]),
  queryFn: () => {
    const filters = Object.fromEntries(
      Object.entries(activeFilters.value).filter(([key, value]) => key !== 'page' && value !== false)
    )

    return getTransactionsAction({
      page: currentPage.value,
      ...filters,
    })
  },
  // keepPreviousData: true,

  staleTime: 1000 * 60 * 3,
})

//  Fetch transaction statistics
const { data: statistics } = useQueryApi({
  queryKey: ['transactionsStatistics'],
  queryFn: getTransactionStatisticsAction,
  staleTime: 1000 * 60 * 5,
})

const transactions = computed(() => transactionsData.value?.data || [])
const pagination = computed(() => transactionsData.value?.pagination)
const totalItems = computed(() => pagination.value?.total || 0)

//  Map API data by slug for easier access
const stats = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const map: Record<string, any> = {}
  if (statistics.value?.data?.length) {
    for (const item of statistics.value.data) {
      map[item.slug] = item
    }
  }
  return map
})

const requisitionsStatus = computed(() => [
  {
    label: 'تراکنش‌های موفق (ماه جاری)',
    price: stats?.value?.finished_transactions?.value ?? 0,
    currency: 'COUNT',
  },
  {
    label: 'جمع مقدار ریالی تراکنش‌های موفق (ماه جاری)',
    price: stats?.value?.sum_finished_transactions?.value ?? 0,
    currency: 'TOMAN',
  },
  {
    label: 'جمع ارزی تراکنش‌های موفق (ماه جاری)',
    price: stats?.value?.sum_currency_amount_finished_transactions?.value ?? 0,
    currency: 'COUNT',
  },
  {
    label: 'تعداد تراکنش اولی‌های (ماه جاری)',
    price: stats?.value?.users_with_first_transactions_within_current_month?.value ?? 0,
    currency: 'COUNT',
  },
  {
    label: 'تعداد تراکنش اولی‌های ثبت نامی ماه جاری',
    price: stats?.value?.users_with_first_transactions_within_current_month_verified?.value ?? 0,
    currency: 'COUNT',
  },
])

//  Filter Fields
const filterFields = computed(() => [
  {
    type: 'input',
    key: 'code',
    label: ' کد تراکنش یا کد کاربری را وارد کنید',
    title: 'جستجو',
  },
  {
    type: 'select',
    key: 'currency',
    label: 'انتخاب نوع ارز',
    title: 'نوع ارز',
    options: currencies.value,
  },
  {
    type: 'select',
    key: 'status',
    label: 'انتخاب وضعیت تراکنش',
    title: 'وضعیت تراکنش',
    options: transactionsStatusOptions.value,
  },
  {
    type: 'date',
    key: 'requestDate',
    title: 'تاریخ آخرین تغییرات',
    rangeKeys: {
      min: 'from_date',
      max: 'till_date',
    },
  },
  {
    type: 'select',
    key: 'origin_bank_account_country',
    label: 'کشور مبدا حساب ارزی',
    title: 'کشور مبدا حساب ارزی',
    options: countries.value,
  },
  {
    type: 'select',
    key: 'destination_bank_account_country',
    label: 'کشور مقصد حساب ارزی',
    title: 'کشور مقصد حساب ارزی',
    options: countries.value,
  },
  {
    type: 'select',
    key: 'discount',
    label: 'انتخاب تخفیف',
    title: 'تخفیف',
    options: discountOptions.value,
  },
])

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onApplyFilters = (values: Record<string, any>) => {
  activeFilters.value = { ...values }
  currentPage.value = 1
  const query = Object.fromEntries(Object.entries(values).filter(([, v]) => v))
  router.push({ path: route.path, query })
}

const onResetFilters = () => {
  activeFilters.value = {}
  currentPage.value = 1
  router.push({ path: route.path, query: {} })
}

const goToPage = (page: number) => {
  currentPage.value = page
  refetchTransactions()
}

onMounted(() => {
  if (Object.keys(route.query).length > 0) {
    const { page, ...filters } = route.query

    activeFilters.value = filters

    if (page) {
      currentPage.value = Number(page)
    }
  }
})
</script>

<template>
  <div class="w-full mb-12">

    <div class="w-full grid grid-cols-1 md:grid-cols-3 gap-6 gap-y-9">
      <template v-for="(stat, index) in requisitionsStatus" :key="`stat-item-${index}`">
        <StatusCard :data="stat" />
      </template>
    </div>

    <h2 class="text-[22px] text-[#1b325a ]  mt-11">لیست تراکنش‌ها</h2>
    <!-- 👉 Filters Section -->
    <Filters :fields="filterFields" @apply="onApplyFilters" @reset="onResetFilters" class="mt-6" />

    <!-- 👉 Main Cards && Pagination  -->
    <div v-if="isTransactionsLoading" class="text-center py-8 text-gray-500">
      در حال دریافت اطلاعات تراکنش‌ها...
    </div>
    <div v-else>
      <div v-if="transactions.length > 0" class="mt-8 flex flex-col gap-6">
        <!-- TransactionCard loop -->
        <TransactionCard v-for="(transaction, index) in transactions" :key="`transaction-${index}`"
          :transaction="transaction" @deleted="refetchTransactions" />

        <Pagination v-model:currentPage="currentPage" :itemsPerPage="pagination?.perPage" :totalItems="totalItems"
          @update:currentPage="goToPage" :isAdmin="true" />
      </div>
      <EmptyState v-else :onReset="onResetFilters" />
    </div>

  </div>
</template>
